import React, { useEffect, useMemo, useState } from 'react';

export interface PasswordStrengthMeterProps {
  password: string;
  className?: string;
  showLabel?: boolean;
  showFeedback?: boolean;
}

interface StrengthResult {
  score: number; // 0-4
  label: string;
  percent: number; // 0-100
  feedback: string[];
}

const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({
  password,
  className,
  showLabel = true,
  showFeedback = false,
}) => {
  const [zxcvbnFn, setZxcvbnFn] = useState<null | ((pw: string) => any)>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mod = await import('@zxcvbn-ts/core');
        const fn = (mod as any).zxcvbn || (mod as any).default;
        if (!cancelled && typeof fn === 'function') setZxcvbnFn(() => fn);
      } catch {
        // fallback silently
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const strength: StrengthResult = useMemo(() => {
    const pw = password || '';
    if (!pw) return { score: -1, label: '', percent: 0, feedback: [] };

    if (zxcvbnFn) {
      const result = zxcvbnFn(pw);
      const labels = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong'];
      const score = Math.max(0, Math.min(4, result.score));
      const percent = ((score + 1) / 5) * 100;
      const feedback: string[] = [];
      if (result.feedback?.warning) feedback.push(result.feedback.warning);
      return { score, label: labels[score], percent, feedback };
    }

    // Fallback heuristic
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    score = Math.max(0, Math.min(4, score));
    const labels = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong'];
    const percent = ((score + 1) / 5) * 100;
    return { score, label: labels[score], percent, feedback: [] };
  }, [password, zxcvbnFn]);

  if (strength.score < 0) return null;

  const colorClass = strength.score <= 1 ? 'bg-rs-red' : strength.score === 2 ? 'bg-yellow-500' : 'bg-rs-green';

  return (
    <div className={className || ''}>
      <div className="h-2 w-full bg-rs-jet rounded">
        <div className={`h-2 rounded ${colorClass}`} style={{ width: `${strength.percent}%` }} />
      </div>
      {showLabel && (
        <div className="text-xs mt-1 text-rs-off-white/80">Strength: {strength.label}</div>
      )}
      {showFeedback && strength.feedback.length > 0 && (
        <ul className="text-[11px] mt-1 text-rs-off-white/70 list-disc ml-4">
          {strength.feedback.slice(0, 2).map((f, i) => (
            <li key={i}>{f}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PasswordStrengthMeter;

